import { AgentOAuth, AgentOAuthConnections } from "./AgentOAuth";

import { clearMentionQueues } from "./TextMentions";
import { useEffect, useRef, useState } from "react";
import {
  ClerkProvider,
  SignInButton,
  SignUpButton,
  useAuth,
  useClerk,
  useUser,
} from "@clerk/react";
import { App } from "./App";
import { Brand } from "./Brand";
import { Welcome, landingButtonClass } from "./Welcome";
import { api, type User } from "./api";
import { startCloudSession, type SessionError } from "./cloud-session";
import {
  captureEvent,
  forgetPerson,
  identifyPerson,
  startProductAnalytics,
  type AnalyticsConfig,
} from "./product-analytics";

type SessionInfo = {
  authMode?: string;
  publishableKey?: string;
  signInAvailable?: boolean;
  analytics?: AnalyticsConfig | null;
};
export function Entry() {
  const [config, setConfig] = useState<SessionInfo | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    document.title = "Calque workspace";
    const description = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    );
    if (description)
      description.content =
        "Sign in to your Calque workspace to open projects and review drawings with your team.";
    api<SessionInfo>("/api/session")
      .then((info) => {
        startProductAnalytics(info.analytics);
        setConfig(info);
      })
      .catch(() => setError("Cannot connect to the workspace server."));
  }, []);
  if (error)
    return (
      <div className="loading">
        <Brand />
        <p>{error}</p>
        <button onClick={() => location.reload()}>Retry</button>
      </div>
    );
  if (!config)
    return (
      <div className="loading">
        <Brand />
        <p>Opening your workspace…</p>
      </div>
    );
  if (config.authMode !== "clerk") return <App />;
  if (!config.signInAvailable || !config.publishableKey)
    return (
      <div className="loading">
        <Brand />
        <p>Sign-in is being configured for this workspace.</p>
      </div>
    );
  return (
    <ClerkProvider
      publishableKey={config.publishableKey}
      appearance={{
        variables: {
          borderRadius: "0px",
          colorPrimary: "#202020",
          fontFamily: '"DM Sans", sans-serif',
        },
      }}
    >
      <CloudIdentity />
    </ClerkProvider>
  );
}
function CloudIdentity() {
  const { isLoaded, isSignedIn, getToken, sessionId } = useAuth();
  const { signOut, openSignIn } = useClerk();
  const { user: clerkUser } = useUser();
  const email = clerkUser?.primaryEmailAddress?.emailAddress;
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<SessionError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [signingOut, setSigningOut] = useState(false);
  const stopRenewal = useRef<() => void>(() => {});
  const newAccount = useRef(false);
  useEffect(() => {
    if (!isLoaded) return;
    const stop = startCloudSession({
      sessionId: isSignedIn ? sessionId : null,
      getToken,
      onUser: setUser,
      onError: setError,
      onNewAccount: () => {
        newAccount.current = true;
      },
    });
    stopRenewal.current = stop;
    return stop;
  }, [isLoaded, isSignedIn, sessionId, getToken, attempt]);
  useEffect(() => {
    if (!user || !isSignedIn) return;
    identifyPerson(user, email);
    // After identify, so the sign-up joins this person's landing-page visit.
    if (newAccount.current) {
      newAccount.current = false;
      captureEvent("signed_up");
    }
  }, [user, isSignedIn, email]);
  async function logout() {
    if (!sessionId) throw new Error("Reload this tab before signing out.");
    setSigningOut(true);
    stopRenewal.current();
    try {
      // Bind both revocations to this tab's session, never another tab's login.
      await api("/api/logout", { sessionId });
      setError(null);
      clearMentionQueues();
      setUser(null);
      forgetPerson();
      await signOut({ sessionId });
    } catch (error) {
      setError({ message: (error as Error).message, requiresSignIn: false });
      setAttempt((n) => n + 1);
      throw error;
    } finally {
      setSigningOut(false);
    }
  }
  async function recoverSignIn() {
    if (!sessionId) return;
    setSigningOut(true);
    try {
      // The server already revoked this session. Only finish its Clerk logout;
      // the shared app cookie may now belong to a healthy session in another tab.
      await signOut({ sessionId });
      openSignIn();
    } catch (error) {
      setError({ message: (error as Error).message, requiresSignIn: true });
    } finally {
      setSigningOut(false);
    }
  }
  if (user && isSignedIn)
    return (
      <>
        {location.pathname === "/oauth/authorize" ? (
          <AgentOAuth key={user.id} userName={user.name} />
        ) : location.pathname === "/oauth/connections" ? (
          <AgentOAuthConnections key={user.id} />
        ) : (
          <App key={user.id} managed={{ user, signOut: logout }} />
        )}
        {error && (
          <div className="toast" role="status">
            Connection interrupted. Keep this tab open while we reconnect.
          </div>
        )}
      </>
    );
  if (!isLoaded || isSignedIn)
    return (
      <div className="loading">
        <Brand />
        {error ? (
          <>
            <p role="alert">{error.message}</p>
            <p>Your workspace is locked until sign-in is verified.</p>
            <div className="identity-actions">
              {!error.requiresSignIn && (
                <button onClick={() => setAttempt((n) => n + 1)}>Retry</button>
              )}
              <button
                disabled={signingOut}
                onClick={() =>
                  void (
                    error.requiresSignIn ? recoverSignIn() : logout()
                  ).catch(() => {})
                }
              >
                {error.requiresSignIn ? "Sign in again" : "Sign out"}
              </button>
            </div>
          </>
        ) : (
          <p>Opening your workspace…</p>
        )}
      </div>
    );
  return (
    <Welcome
      accessOnly
      notice={error && <p role="alert">{error.message}</p>}
      signInAction={
        <SignInButton mode="modal">
          <button className={landingButtonClass("sm", "ghost")}>Sign in</button>
        </SignInButton>
      }
      startAction={(size) => (
        <SignUpButton mode="modal">
          <button
            className={landingButtonClass(size)}
            onClick={() => captureEvent("sign_up_started")}
          >
            Start a project
          </button>
        </SignUpButton>
      )}
      accessNote="Free early access · 2 projects · 1 GiB storage. Request more from your account."
    />
  );
}
